/**
 * Onboarding Session Context
 *
 * Provides onboarding session state to all wizard steps.
 * Acts as a bridge between React Query hooks and step components.
 *
 * Features:
 * - Fetches session on mount
 * - Provides save function with loading/error states
 * - Handles optimistic locking conflicts
 * - Provides navigation helpers
 * - Manages step accessibility
 *
 * Usage:
 * ```tsx
 * function MyStep() {
 *   const { session, saveStep, isSaving, error } = useOnboardingSession();
 *
 *   const handleNext = async () => {
 *     await saveStep(2, { treeName: 'My Tree', treeVisibility: 'private' });
 *     navigate('/onboarding/step-3');
 *   };
 * }
 * ```
 */

import { createContext, useContext, useCallback, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import {
  useFetchOnboardingSession,
  useSaveOnboardingStep,
  isStepAccessible,
  getNextStep,
} from '@/hooks/useOnboarding';
import type {
  FetchOnboardingSessionOutput,
  AnyStepData,
  SaveOnboardingStepOutput,
} from '@/types/onboarding';

// =====================================================
// Context Type Definition
// =====================================================

interface OnboardingSessionContextType {
  // Session data from database
  session: FetchOnboardingSessionOutput | null;

  // Loading states
  isLoadingSession: boolean;
  isSaving: boolean;

  // Error states
  sessionError: Error | null;
  saveError: Error | null;

  // Session status
  hasSession: boolean;
  currentStep: number;
  completedSteps: number[];

  // Actions
  saveStep: (step: number, stepData: AnyStepData) => Promise<SaveOnboardingStepOutput>;
  refetchSession: () => Promise<void>;

  // Navigation helpers
  isStepAccessible: (step: number) => boolean;
  getNextStep: () => number;
}

const OnboardingSessionContext = createContext<OnboardingSessionContextType | undefined>(
  undefined
);

// =====================================================
// Provider Component
// =====================================================

interface OnboardingSessionProviderProps {
  children: ReactNode;
}

export function OnboardingSessionProvider({ children }: OnboardingSessionProviderProps) {
  const { user } = useAuth();

  // Fetch session from database
  const {
    data: session,
    isLoading: isLoadingSession,
    error: sessionError,
    refetch: refetchSessionQuery,
  } = useFetchOnboardingSession(user?.id || '', {
    enabled: !!user?.id,
    refetchOnMount: true,
  });

  // Save step mutation
  const {
    mutateAsync: saveStepMutation,
    isPending: isSaving,
    error: saveError,
    reset: resetSaveError,
  } = useSaveOnboardingStep(user?.id || '');

  // Wrap save function to include version handling
  const saveStep = useCallback(
    async (step: number, stepData: AnyStepData): Promise<SaveOnboardingStepOutput> => {
      // Reset any previous errors
      resetSaveError();

      // Get current version from session (if exists)
      const expectedVersion = session?.version;

      try {
        const result = await saveStepMutation({
          step,
          stepData,
          expectedVersion,
        });

        return result;
      } catch (error) {
        // Error is already handled by mutation, just re-throw
        throw error;
      }
    },
    [session?.version, saveStepMutation, resetSaveError]
  );

  // Refetch session (useful after errors or manual refresh)
  const refetchSession = useCallback(async () => {
    await refetchSessionQuery();
  }, [refetchSessionQuery]);

  // Navigation helpers
  const isStepAccessibleHelper = useCallback(
    (targetStep: number): boolean => {
      if (!session) {
        // If no session, only step 1 is accessible
        return targetStep === 1;
      }

      return isStepAccessible(targetStep, session.completed_steps);
    },
    [session]
  );

  const getNextStepHelper = useCallback((): number => {
    if (!session) {
      return 1; // Start at welcome screen
    }

    return getNextStep(session.completed_steps);
  }, [session]);

  // Derived state
  const hasSession = session !== null;
  const currentStep = session?.current_step || 1;
  const completedSteps = session?.completed_steps || [];

  const value: OnboardingSessionContextType = {
    // Session data
    session: session ?? null,

    // Loading states
    isLoadingSession,
    isSaving,

    // Error states
    sessionError: sessionError as Error | null,
    saveError: saveError as Error | null,

    // Session status
    hasSession,
    currentStep,
    completedSteps,

    // Actions
    saveStep,
    refetchSession,

    // Navigation helpers
    isStepAccessible: isStepAccessibleHelper,
    getNextStep: getNextStepHelper,
  };

  return (
    <OnboardingSessionContext.Provider value={value}>
      {children}
    </OnboardingSessionContext.Provider>
  );
}

// =====================================================
// Hook to use Onboarding Session Context
// =====================================================

/**
 * Access onboarding session context
 *
 * Must be used within OnboardingSessionProvider
 *
 * @throws Error if used outside provider
 */
export function useOnboardingSession() {
  const context = useContext(OnboardingSessionContext);

  if (context === undefined) {
    throw new Error(
      'useOnboardingSession must be used within OnboardingSessionProvider'
    );
  }

  return context;
}
